import { ref, computed } from 'vue';
import axios from '@/plugins/axios';

// global state for system settings
const systemSettings = ref([]);
const isLoading = ref(false);
const lastFetchTime = ref(0);
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export function useSystemSettings() {
  
  // check if cache is valid
  const isCacheValid = computed(() => {
    return Date.now() - lastFetchTime.value < CACHE_DURATION;
  });

  // load system settings with cache
  const loadSystemSettings = async (forceRefresh = false) => {
    // if already loading, wait
    if (isLoading.value && !forceRefresh) {
      return systemSettings.value;
    }

    // if cache is valid and not forced refresh, return cache
    if (!forceRefresh && systemSettings.value.length > 0 && isCacheValid.value) {
      return systemSettings.value;
    }

    try {
      isLoading.value = true;
      const response = await axios.get('/api/system-settings/public-settings');
      
      systemSettings.value = response.data;
      lastFetchTime.value = Date.now();
      
      // save to localStorage
      localStorage.setItem('systemSettings', JSON.stringify(response.data));
      
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar configurações do sistema:', error);
      
      // if failed, try to load from localStorage
      const cached = localStorage.getItem('systemSettings');
      if (cached && systemSettings.value.length === 0) {
        try {
          systemSettings.value = JSON.parse(cached);
          return systemSettings.value;
        } catch (parseError) {
          console.error('Erro ao parsear configurações do cache:', parseError);
        }
      }
      
      return systemSettings.value;
    } finally {
      isLoading.value = false;
    }
  };

  // get a specific setting
  const getSystemSetting = (name) => {
    const setting = systemSettings.value.find(s => s.name === name);
    return setting?.value ?? null;
  };

  // force update of settings
  const refreshSystemSettings = () => {
    return loadSystemSettings(true);
  };

  // clear cache
  const clearCache = () => {
    systemSettings.value = [];
    lastFetchTime.value = 0;
    localStorage.removeItem('systemSettings');
  };

  // initialize loading from localStorage if available
  const initializeFromCache = () => {
    const cached = localStorage.getItem('systemSettings');
    if (cached && systemSettings.value.length === 0) {
      try {
        systemSettings.value = JSON.parse(cached);
        lastFetchTime.value = Date.now() - (CACHE_DURATION / 2); // consider half expired for refresh soon
      } catch (error) {
        console.error('Erro ao carregar configurações do cache:', error);
      }
    }
  };

  // initialize on first time
  if (systemSettings.value.length === 0) {
    initializeFromCache();
  }

  return {
    systemSettings: computed(() => systemSettings.value),
    isLoading: computed(() => isLoading.value),
    isCacheValid,
    loadSystemSettings,
    getSystemSetting,
    refreshSystemSettings,
    clearCache,
    initializeFromCache
  };
} 